import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../axiosSetup";
import DataTable from "./DataTable";

const STATUS_STYLES = {
  offen:               "dt-badge-info",
  "in bearbeitung":    "dt-badge-warn",
  abgeschlossen:       "dt-badge-yes",
  storniert:           "dt-badge-no",
};

const ZRSparePartsList = () => {
  const [allData, setAllData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("open");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchSparePartApplies = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get("/api/all-spareparts-applies-zunftrat", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data || [];

        if (data.length > 0) {
          const first = data[0];
          const hdrs = [{ key: "Anfrager", label: "Anfrager" }];
          for (const key in first) {
            if (key === "Anfrager" || key === "done" || key === "state") continue;
            if (first[key] && typeof first[key] === "object" && "displayName" in first[key]) {
              hdrs.push({ key, label: first[key].displayName });
            } else {
              hdrs.push({ key, label: key });
            }
          }
          hdrs.push({ key: "state", label: "Status" });
          setHeaders(hdrs);
        }
        setAllData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSparePartApplies();
  }, []);

  const openData = useMemo(
    () => allData.filter((item) => !item.done || item.done === 0 || item.done === null),
    [allData]
  );
  const closedData = useMemo(
    () => allData.filter((item) => item.done === 1),
    [allData]
  );

  const columns = useMemo(
    () =>
      headers.map((h) => ({
        key: h.key,
        label: h.label,
        render: (val) => {
          if (h.key === "Anfrager") {
            return val?.Name || <span className="dt-muted">—</span>;
          }
          if (h.key === "state") {
            if (!val) return <span className="dt-muted">—</span>;
            const cls = STATUS_STYLES[String(val).toLowerCase()] || "dt-badge-neutral";
            return <span className={`dt-badge ${cls}`}>{val}</span>;
          }
          if (!val || typeof val !== "object") {
            return val != null ? String(val) : <span className="dt-muted">—</span>;
          }
          const v = val.value;
          if (Array.isArray(v)) {
            return v.length > 0 ? v.join(", ") : <span className="dt-muted">—</span>;
          }
          if (v === true  || v === 1) return <span className="dt-badge dt-badge-yes">Ja</span>;
          if (v === false || v === 0) return <span className="dt-muted">—</span>;
          if (v == null) return <span className="dt-muted">—</span>;
          return String(v);
        },
        searchValue: (val) => {
          if (h.key === "Anfrager") return val?.Name ?? "";
          if (!val || typeof val !== "object") return String(val ?? "");
          const v = val.value;
          if (Array.isArray(v)) return v.join(" ");
          return String(v ?? "");
        },
      })),
    [headers]
  );

  const actions = (item) => (
    <div className="dt-actions">
      <button
        className="dt-btn dt-btn-primary"
        onClick={() =>
          navigate("/service-order-spare-parts", {
            state: { orderId: item.Bestellnummer, from: location.pathname },
          })
        }
      >
        Anfrage bearbeiten
      </button>
    </div>
  );

  const tabConfig = [
    { key: "open", label: "Offene Anfragen", count: openData.length },
    { key: "closed", label: "Abgeschlossene Anfragen", count: closedData.length },
  ];

  const tableData = activeTab === "open" ? openData : closedData;

  return (
    <div className="container-fluid px-3 px-md-4 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Ersatzteil-Bestellungen</h2>
      </div>
      <DataTable
        data={tableData}
        columns={columns}
        loading={loading}
        error={error}
        actions={actions}
        tabs={tabConfig}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        searchPlaceholder="Bestellung suchen…"
        emptyText={activeTab === "open" ? "Keine offenen Bestellungen." : "Keine abgeschlossenen Bestellungen."}
        initialPageSize={25}
        pageSizeOptions={[10, 25, 50]}
      />
      <br />
    </div>
  );
};

export default ZRSparePartsList;
